import { ref, computed } from 'vue'
import { useBuilderApiClient } from '@/api/builder/useBuilderApiClient'
import { ENDPOINTS } from '@/consts/api'
import { postWorkspaceSecret } from '@/api/dashboard/functions/settings'
import { ENDPOINTS as RUN_ENDPOINTS } from '@/modules/workflow-run/api'
import { omit } from 'ramda'
import type { Workspace, WorkspaceOverview, WorkspaceType } from '@/modules/workspaces/types'
import { getWorkspace, updateWorkspace as updateWorkspaceCall, type WorkspaceBodyData } from '@/modules/workspaces/api'
import { useEventBus } from '@vueuse/core'

const currentWorkspace = ref<Workspace>()

const currentWorkspaceId = computed(() => currentWorkspace.value?.id)
const currentWorkspaceType = computed(() => currentWorkspace.value?.workspaceType.toLowerCase() as WorkspaceType | undefined)

const clearCurrentWorkspace = () => {
    currentWorkspace.value = undefined
    localStorage.removeItem('tl-dashboard-workspace-id')
}

const switchCurrentWorkspace = async (workspaceId: string | null) => {
    clearCurrentWorkspace()

    if (workspaceId) {
        try {
            const response = await getWorkspace({ workspaceId }).call()

            if (response) {
                currentWorkspace.value = response.data
                localStorage.setItem('tl-dashboard-workspace-id', workspaceId)
            }
        } catch (e) {
            console.error(e) // eslint-disable-line no-console
        }
    }
}

const updateCurrentWorkspace = async (data: WorkspaceBodyData): Promise<WorkspaceOverview | undefined> => {
    const response = await updateWorkspaceCall({ workspaceId: currentWorkspaceId.value! }, data).call()

    if (response?.data) {
        await switchCurrentWorkspace(response.data.id)
        useEventBus('workspace-updated').emit(response.data)
        return response.data
    }
}

const restoreWorkspace = async () => {
    const savedWorkspaceId = localStorage.getItem('tl-dashboard-workspace-id')

    if (savedWorkspaceId) {
        await switchCurrentWorkspace(savedWorkspaceId)
    }

    return savedWorkspaceId
}

const generateNewSecret = async (workspaceId: string) => {
    const response = await postWorkspaceSecret({
        workspaceId,
        workspaceType: currentWorkspaceType.value!,
    }).call()

    return response?.data.newSecret
}

const generateLink = async (
    workspaceId: string | undefined,
    payload: Record<string, unknown>,
    headers?: Record<string, string>,
    useV3Endpoint?: boolean,
    locale?: string,
    expiresAt?: string,
): Promise<Record<'link', string>> => {
    if (useV3Endpoint) {

        const { call } = useBuilderApiClient<Record<'link', string>>({
            method: 'POST',
            url: RUN_ENDPOINTS.WORKFLOWS,
            urlParams: {
                workspaceId: workspaceId as string,
            },
            data: {
                ...omit(['parametersId'], payload),
                ...locale ? { locale } : {},
                ...expiresAt ? { expiresAt } : {},
            },
        })

        const response = await call()

        return { link: response?.data.link || '' }
    }

    const { call } = useBuilderApiClient<Record<'link', string>>({
        method: 'POST',
        url: ENDPOINTS.LINK,
        urlParams: {
            workspaceId: workspaceId as string,
        },
        data: {
            ...payload,
        },
        ...headers ? { headers } : {},
    })

    const response = await call()

    return { link: response?.data.link || '' }
}

export function useWorkspace() {
    return {
        currentWorkspaceId,
        currentWorkspace,
        currentWorkspaceType,
        switchCurrentWorkspace,
        updateCurrentWorkspace,
        restoreWorkspace,
        generateNewSecret,
        generateLink,
    }
}
